// Achievement tracking and milestone calculation utilities
import { startOfWeek } from 'date-fns';

// Achievement definitions
export const ACHIEVEMENT_TYPES = {
  TASK_MILESTONE: 'task_milestone',
  STREAK: 'streak',
  WEEKLY_STARS: 'weekly_stars'
};

export const ACHIEVEMENTS = [
  {
    id: 'tasks_10',
    type: ACHIEVEMENT_TYPES?.TASK_MILESTONE,
    title: '10 Tasks Completed',
    description: 'Complete 10 tasks',
    icon: 'check-circle',
    color: '#10B981',
    threshold: 10,
    reward: 'badge'
  },
  {
    id: 'tasks_25',
    type: ACHIEVEMENT_TYPES?.TASK_MILESTONE,
    title: '25 Tasks Completed',
    description: 'Complete 25 tasks',
    icon: 'award',
    color: '#3B82F6',
    threshold: 25,
    reward: 'medal'
  },
  {
    id: 'tasks_50',
    type: ACHIEVEMENT_TYPES?.TASK_MILESTONE,
    title: '50 Tasks Completed',
    description: 'Complete 50 tasks',
    icon: 'trophy',
    color: '#F59E0B',
    threshold: 50,
    reward: 'trophy'
  },
  {
    id: 'tasks_100',
    type: ACHIEVEMENT_TYPES?.TASK_MILESTONE,
    title: '100 Tasks Completed',
    description: 'Complete 100 tasks',
    icon: 'crown',
    color: '#A855F7',
    threshold: 100,
    reward: 'crown'
  },
  {
    id: 'streak_3',
    type: ACHIEVEMENT_TYPES?.STREAK,
    title: '3-Day Streak',
    description: 'Complete tasks for 3 days in a row',
    icon: 'flame',
    color: '#EF4444',
    threshold: 3,
    reward: 'star'
  },
  {
    id: 'streak_7',
    type: ACHIEVEMENT_TYPES?.STREAK,
    title: '7-Day Streak',
    description: 'Complete tasks for 7 days in a row',
    icon: 'zap',
    color: '#F59E0B',
    threshold: 7,
    reward: 'medal'
  },
  {
    id: 'streak_14',
    type: ACHIEVEMENT_TYPES?.STREAK,
    title: '14-Day Streak',
    description: 'Complete tasks for 14 days in a row',
    icon: 'flame',
    color: '#A855F7',
    threshold: 14,
    reward: 'trophy'
  },
  {
    id: 'streak_30',
    type: ACHIEVEMENT_TYPES?.STREAK,
    title: '30-Day Streak',
    description: 'Complete tasks for 30 days in a row',
    icon: 'zap',
    color: '#10B981',
    threshold: 30,
    reward: 'crown'
  },
  {
    id: 'weekly_stars_10',
    type: ACHIEVEMENT_TYPES?.WEEKLY_STARS,
    title: '10 Stars This Week',
    description: 'Earn 10 stars in one week',
    icon: 'star',
    color: '#F59E0B',
    threshold: 10,
    reward: 'star'
  },
  {
    id: 'weekly_stars_25',
    type: ACHIEVEMENT_TYPES?.WEEKLY_STARS,
    title: '25 Stars This Week',
    description: 'Earn 25 stars in one week',
    icon: 'sparkles',
    color: '#A855F7',
    threshold: 25,
    reward: 'gem'
  },
  {
    id: 'weekly_stars_50',
    type: ACHIEVEMENT_TYPES?.WEEKLY_STARS,
    title: '50 Stars This Week',
    description: 'Earn 50 stars in one week',
    icon: 'star',
    color: '#EF4444',
    threshold: 50,
    reward: 'crown'
  }
];

// Calculate total completed tasks for a member
export const calculateCompletedTasks = (tasks, memberId) => {
  return tasks?.filter(task => 
    (task?.personId === memberId || task?.assignedTo === memberId) && 
    task?.completed === true
  )?.length || 0;
};

// Calculate current streak for a member
export const calculateStreak = (tasks, memberId) => {
  const completedTasks = tasks
    ?.filter(task => 
      (task?.personId === memberId || task?.assignedTo === memberId) && 
      task?.completed === true &&
      task?.completedAt
    )
    ?.sort((a, b) => new Date(b?.completedAt) - new Date(a?.completedAt));

  if (!completedTasks?.length) return 0;

  let streak = 0;
  let currentDate = new Date();
  currentDate?.setHours(0, 0, 0, 0);

  const uniqueDays = new Set();
  completedTasks?.forEach(task => {
    const taskDate = new Date(task?.completedAt);
    taskDate?.setHours(0, 0, 0, 0);
    uniqueDays?.add(taskDate?.getTime());
  });

  const sortedDays = Array?.from(uniqueDays)?.sort((a, b) => b - a);

  for (let i = 0; i < sortedDays?.length; i++) {
    const dayTime = sortedDays?.[i];
    const expectedTime = new Date(currentDate);
    expectedTime?.setDate(expectedTime?.getDate() - i);
    expectedTime?.setHours(0, 0, 0, 0);

    if (dayTime === expectedTime?.getTime()) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
};

// Calculate weekly stars for a member
export const calculateWeeklyStars = (tasks, memberId) => {
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  
  return tasks
    ?.filter(task => {
      const isAssigned = task?.personId === memberId || task?.assignedTo === memberId;
      const isCompleted = task?.completed === true;
      const hasStars = task?.stars > 0;
      
      if (!isAssigned || !isCompleted || !hasStars || !task?.completedAt) return false;
      
      const completedDate = new Date(task?.completedAt);
      return completedDate >= weekStart;
    })
    ?.reduce((total, task) => total + (task?.stars || 0), 0) || 0;
};

// Check which achievements a member has unlocked
export const checkAchievements = (tasks, memberId, previousAchievements = []) => {
  const completedCount = calculateCompletedTasks(tasks, memberId);
  const currentStreak = calculateStreak(tasks, memberId);
  const weeklyStars = calculateWeeklyStars(tasks, memberId);

  const newlyUnlocked = [];
  const allUnlocked = [...previousAchievements];

  ACHIEVEMENTS?.forEach(achievement => {
    const alreadyUnlocked = previousAchievements?.some(a => a?.id === achievement?.id);
    
    if (alreadyUnlocked) return;

    let isUnlocked = false;

    switch (achievement?.type) {
      case ACHIEVEMENT_TYPES?.TASK_MILESTONE:
        isUnlocked = completedCount >= achievement?.threshold;
        break;
      case ACHIEVEMENT_TYPES?.STREAK:
        isUnlocked = currentStreak >= achievement?.threshold;
        break;
      case ACHIEVEMENT_TYPES?.WEEKLY_STARS:
        isUnlocked = weeklyStars >= achievement?.threshold;
        break;
      default:
        break;
    }

    if (isUnlocked) {
      const unlockedAchievement = {
        ...achievement,
        unlockedAt: new Date()?.toISOString()
      };
      newlyUnlocked?.push(unlockedAchievement);
      allUnlocked?.push(unlockedAchievement);
    }
  });

  return { newlyUnlocked, allUnlocked };
};

// Get achievement progress for a member
export const getAchievementProgress = (tasks, memberId, achievementId) => {
  const achievement = ACHIEVEMENTS?.find(a => a?.id === achievementId);
  if (!achievement) return 0;

  let current = 0;

  switch (achievement?.type) {
    case ACHIEVEMENT_TYPES?.TASK_MILESTONE:
      current = calculateCompletedTasks(tasks, memberId);
      break;
    case ACHIEVEMENT_TYPES?.STREAK:
      current = calculateStreak(tasks, memberId);
      break;
    case ACHIEVEMENT_TYPES?.WEEKLY_STARS:
      current = calculateWeeklyStars(tasks, memberId);
      break;
    default:
      break;
  }

  return Math?.min(100, (current / achievement?.threshold) * 100);
};

// Get member statistics
export const getMemberStats = (tasks, memberId) => {
  return {
    totalCompleted: calculateCompletedTasks(tasks, memberId),
    currentStreak: calculateStreak(tasks, memberId),
    weeklyStars: calculateWeeklyStars(tasks, memberId)
  };
};
